import React, {  useEffect, useState, useCallback, useMemo, useRef } from 'react'
import useMediaDataAndTabs from '../Components/commonV3/hooks/useMediaDataAndTabs';
import { MediaProvider, NavigationProvider, UIProvider } from '../Components/commonV3/context';
import MainViewer from '../Components/commonV3/MainViewer';
import BottomSlider from '../Components/commonV3/BottomSlider';
import RightGrid from '../Components/commonV3/RightGrid';
import useCarouselNavigation from '../Components/commonV3/hooks/useCarouselNavigation';
import { IMAGE_COMPRESSOR } from '../utils/imageCompressor';
import { ASSETS, TAB_ENUM } from '../Components/common/config';
import AnimatedLoader from '../Components/common/Loader';
import FullScreenModal from '../Components/commonV3/FullScreenModal';
import ShareModal from '../Components/commonV3/shareModal/ShareModal';
import Tabs from '../Components/common/Tabs';
import { captureMetrics } from '../utils/captureMetrics';
import getUserMetrics from '../utils/getUserMetrics';

const CommonIframeV3 = () => {
    const { skuData, loading, selectedTemplate, selectedTab, tabs, carouselItems, setSelectedTab, dynamicTabImage, aspectRatio, skuName, mediaId, isMobile, enterpriseId, dealerVinId, sessionId, teamId, executionTime, apiCallFlag, isFirstSixExt } = useMediaDataAndTabs();
    const { selectedIndex, handleLeft, handleRight, handleSelectThumbnail, skipAnimation } = useCarouselNavigation({ carouselItems, selectedTab, setSelectedTab });
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [isSingleProduct, setIsSingleProduct] = useState(false);
    const [showOverlays, setShowOverlays] = useState(true);
    const [engagementStartTime, setEngagementStartTime] = useState(null);
    const hasStartedRef = useRef(false); // Keeps track without triggering re-renders
    const pauseStartRef = useRef(null);
    const totalPausedDurationRef = useRef(0);
    const pageViewStartTimeRef = useRef(null);
    const isFirstRenderRef = useRef(true)
    const [engagement, setEngagement] = useState({
        spin: 0,
        video: 0,
        image: 0,
    })
    const [prevIndex, setPrevIndex] = useState(selectedIndex)
    const [imageViewingTimeArray, setImageViewingTimeArray] = useState([]);
    const [totalTimeSpent, setTotalTimeSpent] = useState(0);
    const [timeSpentByImages, setTimeSpentByImages] = useState(0)
    const [timeSpentBy360, setTimeSpentBy360] = useState(0)
    const currentViewStartTimeRef = useRef(0);

    useEffect(() => {
        pageViewStartTimeRef.current = Date.now();
    }, []);

    const sendViewAnalytics = async () => {
        const userMetrics = await getUserMetrics()

        const analyticsData = {
            commonIframeLoadingTime: executionTime,
            methodOfIframeData: apiCallFlag,
            ...userMetrics,
        };
        captureMetrics({dealerVinId, teamId, analyticsData, apiCallFlag, sessionId}); 
    }

    useEffect(() => {
        if(teamId && executionTime && apiCallFlag && sessionId) {
            sendViewAnalytics();
        }
    },[teamId, executionTime, apiCallFlag, sessionId]);

    const calculateMedian = (numbers) => {
        if (numbers.length === 0) return 0;
        const sorted = [...numbers].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 === 0
          ? (sorted[mid - 1] + sorted[mid]) / 2
          : sorted[mid];
      };
    
      
    const handleRecordEngagement = (product) => {
        // Check if engagement for the product is already recorded as 1
        if (engagement[product] === 1) {
            return; // Skip if already recorded
        }

        // Update engagement state
        let curEngagement = {
            ...engagement,
            [product]: 1
        };
        
        setEngagement(curEngagement);

        // Call captureMetrics since this is a new engagement
        captureMetrics({ sessionId, teamId, engagement:curEngagement });
      };

      const handleUpdateTimeSpentBy360 = (updatedTimeSpent) =>{
        setTimeSpentBy360(prev => prev + updatedTimeSpent);
      }

      const handleUpdateImageViewingTimeArray = (viewingTime) => {
        setImageViewingTimeArray((prev) => {
          const existingIndex = prev.findIndex(item => item.indexVisited === prevIndex);
          if (existingIndex !== -1) {
            // If index already exists, update its viewing time
            const updatedArray = [...prev];
            updatedArray[existingIndex] = {
              ...updatedArray[existingIndex],
              viewingTime: updatedArray[existingIndex].viewingTime + (viewingTime/1000)
            };
            return updatedArray;
          } else {
            // If index is new, add new entry
            return [
              ...prev,
              { 
                indexVisited: prevIndex, 
                viewingTime: (viewingTime)/1000 
              }
            ];
          }
        });
      }

      useEffect(()=> {
        const currTimeSpent = timeSpentBy360 + timeSpentByImages
        setTotalTimeSpent(currTimeSpent)
        hasStartedRef.current && captureMetrics({sessionId, teamId, timeSpent: currTimeSpent})
      },[timeSpentBy360, timeSpentByImages])

    useEffect(()=> {
        if(isFirstRenderRef.current){
            isFirstRenderRef.current = false;
            return;
        }
        selectedTab === "image" && handleRecordEngagement("image")

       const now = Date.now();
       const viewingTime = now - (currentViewStartTimeRef.current || now);
       currentViewStartTimeRef.current = now;

        // Update the viewing times array
        viewingTime !== 0 && handleUpdateImageViewingTimeArray(viewingTime)
        setPrevIndex(selectedIndex)
        
    }, [selectedIndex])

    useEffect(()=> {
        // // Calculate median and number of visited indexes
        const allViewingTimes = imageViewingTimeArray.map((item) => item.viewingTime)
        const median = calculateMedian(allViewingTimes);
        const visitedIndexes = allViewingTimes.length;

        // Compute the time to send to handleUpdateTimeSpent
        const timeToSend = median * visitedIndexes;
        setTimeSpentByImages(timeToSend)

    },[imageViewingTimeArray])


    const handleBeforeUnload = () => {
        if(pageViewStartTimeRef?.current > 0){
            const now = Date.now();
            let pausedDuration = totalPausedDurationRef.current || 0;
            
            // If the tab is currently blurred (user left without returning), add this final pause duration
            if (pauseStartRef?.current) {
                const finalPauseDuration = now - pauseStartRef.current;
                pausedDuration += finalPauseDuration;
            }
            
            const totalViewDuration = now - pageViewStartTimeRef.current;
            const activeViewDuration = totalViewDuration - pausedDuration;
            const storedTeamId = sessionStorage.getItem('teamId');
            captureMetrics({
                sessionId, 
                teamId: storedTeamId, 
                totalViewDuration: (totalViewDuration/1000), 
                activeViewDuration: (activeViewDuration/1000), 
                unLoadFlag: true
            });
        }
        sessionStorage.removeItem('sessionId');
        sessionStorage.removeItem('teamId');
    };

    useEffect(() => {
        const handleBlur = () => {
            pauseStartRef.current = Date.now();
        };

        const handleFocus = () => {
            if (pauseStartRef?.current) {
                const now = Date.now();
                const pauseDuration = now - pauseStartRef?.current;
                totalPausedDurationRef.current += pauseDuration;
                pauseStartRef.current = null;            
            }
        };

        // For desktop browsers
        window.addEventListener('beforeunload', handleBeforeUnload);
        
        // For mobile browsers
        window.addEventListener('blur', handleBlur);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                // Save metrics when page becomes hidden (common mobile scenario)
                handleBeforeUnload();
            }
        });
        
        return () => {
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('visibilitychange', handleBeforeUnload);
        };
    }, []);
    // Start engagement time on first interaction
    const handleStartEngagementTime = () => {
        if (!hasStartedRef.current) {
            const now = Date.now();
            setEngagementStartTime(now);
            hasStartedRef.current = true;
        }
    };

    const handleShareModal = useCallback(() => {
        setShareModalOpen(true);
    }, []);

    const navigationValue = {
        carouselItems,
        selectedIndex,
        handleLeft,
        handleRight,
        handleSelectThumbnail,
        skipAnimation,
        setSelectedTab
    }
    const uiValue = {
        isSingleProduct,
        showOverlays,
        setShowOverlays,
        handleShareModal,
        handleRecordEngagement,
        sessionId,
        handleUpdateTimeSpentBy360,
        handleStartEngagementTime
    };

    useEffect(() => {
        if (tabs?.length === 1) {
            if( (tabs[0]?.key === TAB_ENUM.VIDEO_VIEW ||  tabs[0]?.key === TAB_ENUM.THREE_SIXTY_VIEW)){
                setIsSingleProduct(true);
            }else{
                setIsSingleProduct(false);
            }
        }else{
            setIsSingleProduct(false);
        }
    }, [tabs]);

   

    const handleFullScreen = () => {
        setIsFullScreen(true);
    };

    useEffect(() => {
        if(isFullScreen === true){
        // alert(selectedTab)
        handleSelectThumbnail(selectedIndex,selectedTab);
        window.parent.postMessage(
            { type: 'FULLSCREEN_MODE_CHANGED', isFullScreen, selectedIndex, selectedTab },
            '*'
        );

    }
    }, [isFullScreen]);

    const handleMessage = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event?.data && typeof event?.data?.closeFullScreen !== "undefined") {
            setIsFullScreen(false);
            handleSelectThumbnail(event?.data?.selectedIndex,event?.data?.selectedTab);
        }else if(event?.data && typeof event?.data?.showFullScreenModal !== "undefined"){
            setIsFullScreen(true);
            handleSelectThumbnail(event?.data?.selectedIndex,event?.data?.selectedTab);
        }
      };

    useEffect(()=> {
        window.addEventListener("message", handleMessage);
        return () => {
            window.removeEventListener("message", handleMessage)
        }
    },[])


    // console.log(imageViewingTimeArray, "imageviewingtimearray")
    // console.log(timeSpentBy360, "timespentby360")
    // console.log(timeSpentByImages, "timespentbyimages")
    // console.log(totalTimeSpent, "totaltimespent")
    // console.log(engagement, "engagemet")

    return (
        !loading && (!skuData.isMediaPresent)) ?
        <img className="w-full h-full max-sm:object-contain" src={IMAGE_COMPRESSOR({ url: ASSETS.other.noSkuDone })} alt="vin is in process" />
        : (
            <>
                {
                    loading ? <AnimatedLoader /> :
                    <MediaProvider value={{skuData,
                        loading,
                        selectedTemplate,
                        selectedTab,
                        tabs,
                        dynamicTabImage,
                        aspectRatio,
                        skuName,
                        mediaId,
                        enterpriseId,
                        dealerVinId}}>
                        <NavigationProvider value={navigationValue}>
                            <UIProvider value={uiValue}>
                            {!isMobile ? (
                                <div className={`w-full h-full relative flex flex-col overflow-hidden`} onClick={handleStartEngagementTime}>
                                    {selectedTemplate === "t1" ? (
                                        <div className={`w-full h-dvh flex flex-col overflow-hidden ${isSingleProduct && ''}`}>
                                            <div className={`w-full ${isSingleProduct ? 'max-h-full' : 'max-h-[85%] pt-2 '} `} style={{
                                                aspectRatio: aspectRatio
                                            }}>
                                                <MainViewer template={selectedTemplate} handleFullScreen={handleFullScreen} isFullScreen={isFullScreen} dynamicTabImage={dynamicTabImage} />
                                            </div>
                                            {
                                                !isSingleProduct && (
                                                    <div className='w-full min-h-[15%] max-h-[40%] h-full overflow-x-scroll'>
                                                        <BottomSlider />
                                                    </div>
                                                )
                                            }
                                        </div>

                                    ) : (
                                        <div className={`w-screen h-dvh flex ${isSingleProduct && ' py-2 items-center justify-center'}`}>
                                            <div className={`h-full ${isSingleProduct ? 'max-w-full' : 'max-w-[80%] p-2'}`} style={{
                                                aspectRatio: aspectRatio
                                            }}>
                                                <MainViewer template={selectedTemplate} handleFullScreen={handleFullScreen} isFullScreen={isFullScreen} dynamicTabImage={dynamicTabImage} />
                                            </div>
                                            {
                                                !isSingleProduct && (
                                                    <div className='w-full h-full max-w-[80%] overflow-y-scroll scrollbar-hide'>
                                                        <RightGrid />
                                                    </div>
                                                )
                                            }

                                        </div>
                                    )}
                                    {/* <FullScreenModal isOpen={isFullScreen} onClose={() => setIsFullScreen(false)} carouselItems={carouselItems} /> */}

                                    {/* {
                                        shareModalOpen && <ShareModal onClose={() => setShareModalOpen(false)} downloadEnabled={true} />
                                    } */}
                                </div> 
                            ) : (
                                <div className={`w-full h-full relative flex flex-col overflow-hidden`} onClick={handleStartEngagementTime}>

                                    <MainViewer template={"mobile"} handleFullScreen={()=>{}} isFullScreen={false} dynamicTabImage={dynamicTabImage} isFirstSixExt={isFirstSixExt} />
                                    <div className={`bg-blue-lightestBlue shadow-md shadow-black-8 sm:rounded-b-md md:rounded-2xl max-md:w-full md:w-max z-[999]`}>
                                        {tabs?.length > 1 && <Tabs
                                            options={tabs}
                                            onSelect={(option) => {
                                                setSelectedTab(option?.key);
                                                const firstItemIndex = carouselItems.findIndex(item => item.type === option?.key);
                                                if (firstItemIndex !== -1) {
                                                    handleSelectThumbnail(firstItemIndex, option?.key)
                                                }
                                            }}
                                            isV3={true}
                                            selectedTab={selectedTab}
                                        />}
                                    </div>

                                </div>
                            )}    
                    </UIProvider>
                    </NavigationProvider>
                </MediaProvider>
                }
            </>
        )
}

export default CommonIframeV3